import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api, refreshApp } from "@/lib/api";
import { onDirty, setRemoteChange } from "@/lib/cache";
import { useAuth } from "@/lib/auth";
import { prefetchData } from "@/lib/prefetch";

const latestChange = () => api.get("/changes/latest", { fresh: true }).then((r) => r.data.latest || "").catch(() => null);

// Screens reuse what they already loaded; this pulls everything fresh. A dot shows when something changed elsewhere
// (an assistant, the extension, another device) since this screen last loaded.
export default function RefreshButton() {
  const [spin, setSpin] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const { user } = useAuth();
  const base = useRef(null);
  const resync = () => latestChange().then((v) => { if (v !== null) { base.current = v; setRemoteChange(v); setWaiting(false); } });
  useEffect(() => {
    resync();
    let t2;
    const offDirty = onDirty(() => { clearTimeout(t2); t2 = setTimeout(resync, 2500); }); // my own saves don't count
    const t = setInterval(() => {
      if (document.hidden || base.current === null) return;
      latestChange().then((v) => {
        if (!v || v <= base.current) return;
        setWaiting(true);
        setRemoteChange(v);
        prefetchData(user); // new uploads elsewhere: bring the phone's copies up to date in the background
      });
    }, 2 * 60000);
    return () => { clearInterval(t); clearTimeout(t2); offDirty(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!spin) return undefined; const t = setTimeout(() => setSpin(false), 900); return () => clearTimeout(t); }, [spin]);
  return (
    <button type="button" onClick={() => { setSpin(true); setWaiting(false); refreshApp(); }} data-testid="app-refresh" data-waiting={waiting ? "true" : "false"}
      aria-label={waiting ? "Updates waiting. Refresh" : "Refresh"} title={waiting ? "Something changed. Tap to see it" : "Refresh from the server"}
      className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50">
      <RefreshCw className={`h-4 w-4 ${spin ? "animate-spin" : ""}`} />
      {waiting && <span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" data-testid="app-refresh-dot" />}
    </button>
  );
}
